import clsx from "clsx";
import { AnimatePresence, useInView } from "framer-motion";
import { motion } from "framer-motion";
import { graphql } from "gatsby";
import { sortBy } from "lodash";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDataLayer } from "../../../hooks/use-data-layer";
import { useWindowSize } from "../../../hooks/use-window-size";
import { GoogleAnalyticsEvent } from "../../../utils/analytics";
import { Cta } from "../../buttons/cta";
import { CompanySiteListItem } from "./company-site-list-item";
import { Earth, EarthControls, WorldMapMarker } from "./earth";
import { WorldAreas, WorldAreaTabsKeys } from "./types";
import { WorldAreaTabs } from "./world-area-tabs";
import { WorldCanvasWrapper } from "./world-canvas-wrapper";

export interface WorldMapProps extends Queries.WorldMapDataFragment {}

export const WorldMap: FC<WorldMapProps> = ({
  title,
  body,
  locatorTitle,
  companySites,
  cta,
  showGlobe,
}) => {
  const earthRef = useRef<EarthControls>(null);
  // Filters tabs that have no elements
  const tabsKeys: WorldAreaTabsKeys[] = [
    companySites?.some((site) => site?.isHQ) && "HQ",
    ...Object.values(WorldAreas).map(
      (area) => companySites?.some((site) => site?.worldArea === area) && area
    ),
  ].filter((item): item is WorldAreaTabsKeys => !!item);

  const firstNonEmptyAreaId = tabsKeys[0] ?? "";
  const [currentAreaId, setCurrentAreaId] = useState<WorldAreaTabsKeys>(firstNonEmptyAreaId);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [isRotationEnabled, setRotationEnabled] = useState(true);
  const { width: windowWidth } = useWindowSize();

  const rootRef = useRef<HTMLDivElement>(null);
  const siteListRootRef = useRef<HTMLDivElement>(null);
  const sitesRef = useRef<Array<HTMLDivElement | null>>([]);
  const [sectionIndicatorHeight, setSectionIndicatorHeight] = useState(0);
  const [sectionIndicatorTop, setSectionIndicatorTop] = useState(0);
  const isInView = useInView(rootRef, { once: true, amount: 0 });

  const orderedCurrentAreaSites = useMemo(
    () =>
      sortBy(
        (companySites ?? []).filter(
          (site) => site && (currentAreaId === "HQ" ? site.isHQ : site.worldArea === currentAreaId)
        ),
        (x) => x?.order ?? Infinity,
        (x) => x?.name?.toLowerCase()
      ),
    [currentAreaId, companySites]
  );

  const markers = useMemo(() => {
    return (companySites ?? [])
      .map((site) => {
        return {
          lat: site!.lat,
          lng: site!.lng,
          id: site!.id,
        };
      })
      .flat()
      .filter(({ lat, lng }) => lat !== null && lng !== null) as WorldMapMarker[];
  }, [companySites]);

  const onClickMarker = useCallback(
    (id: string) => {
      const site = (companySites ?? []).find((site) => site?.id === id);
      if (site?.worldArea) {
        setCurrentAreaId(site.worldArea as WorldAreaTabsKeys);
        setSelectedSiteId(id);
        earthRef.current?.moveCameraToMarker(id);
      }
    },
    [companySites]
  );

  const onClickCompanySiteItem = useCallback((id: string) => {
    setSelectedSiteId(id);
    earthRef.current?.moveCameraToMarker(id);
  }, []);

  const onClickTab = useCallback(
    (tabId: WorldAreaTabsKeys) => {
      if (currentAreaId !== tabId) {
        setCurrentAreaId(tabId);
        setSelectedSiteId("");
      }
    },
    [currentAreaId]
  );

  const getEarthContent = useCallback(
    (variant: "mobile" | "desktop") => {
      const earthRadius = variant === "desktop" ? Math.min((windowWidth / 1440) * 4, 4) : 4;

      return isInView ? (
        <Earth
          ref={earthRef}
          radius={earthRadius}
          markers={markers}
          onClickMarker={onClickMarker}
          reactivateRotationAfterMs={2500}
          {...{
            isRotationEnabled,
            setRotationEnabled,
            selectedMarkerId: selectedSiteId,
          }}
        />
      ) : null;
    },
    [isRotationEnabled, isInView, markers, onClickMarker, selectedSiteId, windowWidth]
  );

  const selectedSiteIndex = orderedCurrentAreaSites.findIndex((s) => s?.id === selectedSiteId);
  useEffect(() => {
    const selectedSiteRect = sitesRef.current[selectedSiteIndex]?.getBoundingClientRect();
    const rootRect = siteListRootRef.current?.getBoundingClientRect();
    setSectionIndicatorHeight(selectedSiteRect?.height ?? 0);
    setSectionIndicatorTop(
      (selectedSiteRect?.top ?? 0) -
        (rootRect?.top ?? 0) +
        (siteListRootRef.current?.scrollTop ?? 0)
    );
  }, [selectedSiteIndex]);

  const { pushEvent } = useDataLayer();
  useEffect(() => {
    if (selectedSiteId) {
      const selectedSite = companySites?.find((site) => site?.id === selectedSiteId);
      pushEvent({
        event: GoogleAnalyticsEvent.ClickLocation,
        location: selectedSite?.city,
        locator_type: selectedSite?.siteType,
      });
    }
  }, [selectedSiteId, pushEvent, companySites]);

  return (
    <div
      className={clsx(
        "bg-primary",
        showGlobe ? "lg:pb-8 pt-20 lg:pt-44 lg:px-20" : "py-12 lg:py-24 px-8 xl:px-40"
      )}
      ref={rootRef}
    >
      {/* Main title */}
      {title && (
        <h2
          className={clsx(
            showGlobe ? "lg:w-[75%]" : "text-center",
            "text-neutral text-5xl lg:text-8xl pb-12 px-8 lg:px-0 leading-snug"
          )}
        >
          {title}
        </h2>
      )}

      {/* Body text */}
      {body && (
        <p
          className={clsx(
            showGlobe ? "lg:text-right lg:ml-auto lg:w-[50%]" : "text-center",
            "text-neutral text-xl lg:text-2xl",
            "pb-12 px-8 lg:px-0 font-thin"
          )}
        >
          {body.body}
        </p>
      )}

      {/* Map locator title */}
      {locatorTitle && (
        <h3
          className={clsx(
            !showGlobe && "text-center",
            "text-neutral text-3xl lg:text-4xl pb-6 px-8"
          )}
        >
          {locatorTitle}
        </h3>
      )}

      {/* Main content */}
      <div
        className={clsx(
          "flex flex-col-reverse lg:flex-row items-center lg:items-start w-full",
          showGlobe && "bg-dark rounded-3xl h-screen lg:h-[85vh]"
        )}
      >
        <div
          className={clsx(
            "w-full flex flex-col",
            showGlobe && "lg:w-[40%] lg:pr-0 h-[50%] lg:h-full",
            "text-neutral px-8 lg:px-12 pt-4 pb-8 lg:pb-12"
          )}
        >
          {/* Tabs */}
          <WorldAreaTabs
            shouldPlayAnimation={isInView}
            selectedTabId={currentAreaId}
            onClickTab={onClickTab}
            tabsKeys={tabsKeys}
            variant={showGlobe ? "default" : "pills"}
          />
          {/* List of company sites for selected area */}
          <div
            className="flex flex-col overflow-y-auto light-scrollbar flex-1 relative"
            ref={siteListRootRef}
          >
            <AnimatePresence>
              {sectionIndicatorHeight > 0 && (
                <motion.span
                  initial={{ transform: "scaleY(0)" }}
                  animate={{ transform: "scaleY(100%)" }}
                  key="siteIndicator"
                  className="absolute bottom-0 block w-1 bg-primary transition-all duration-300"
                  style={{ top: sectionIndicatorTop, height: sectionIndicatorHeight }}
                />
              )}
            </AnimatePresence>
            <div className={clsx("grid gap-x-16", showGlobe ? "grid-cols-1" : "lg:grid-cols-2")}>
              {orderedCurrentAreaSites.map((site, i) => (
                <CompanySiteListItem
                  ref={(el) => (sitesRef.current[i] = el)}
                  index={i}
                  shouldAnimate={isInView}
                  site={site!}
                  key={`${site!.id}-${i}`}
                  onClick={() => site && onClickCompanySiteItem(site.id)}
                  className={clsx("pl-4", showGlobe && "cursor-pointer")}
                  variant={showGlobe ? "darkBackground" : "primaryBackground"}
                />
              ))}
            </div>
          </div>
          {/* CTA */}
          <div className={clsx("flex", showGlobe ? "pt-6" : "justify-center pt-16")}>
            {cta && <Cta variant="light-outline" {...cta} />}
          </div>
        </div>
        {/* World canvas */}
        {showGlobe && (
          <>
            <div className="h-full w-full lg:w-[60%] flex-1 hidden lg:block relative">
              <WorldCanvasWrapper variant="desktop">
                {getEarthContent("desktop")}
              </WorldCanvasWrapper>
            </div>
            <div className="h-[50vh] w-full lg:w-[60%] flex-1 lg:hidden relative">
              <WorldCanvasWrapper variant="mobile">{getEarthContent("mobile")}</WorldCanvasWrapper>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const query = graphql`
  fragment CompanySiteData on ContentfulCompanySite {
    id
    name
    address
    zipCode
    city
    state
    country
    lat
    lng
    worldArea
    isHQ
    order
    siteType
  }

  fragment WorldMapData on ContentfulWorldMap {
    title
    showGlobe
    body {
      body
    }
    locatorTitle
    node_locale
    companySites {
      ...CompanySiteData
    }
    cta {
      ...CtaData
    }
  }
`;
